import { useEffect, useMemo, useState } from "react";
import { Plus, Trash2, ChevronLeft, ChevronRight, Edit2, Check, X, Mail, Clock, Copy } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import Modal from "@/components/site/Modal";

function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function addMonths(d, n) { return new Date(d.getFullYear(), d.getMonth() + n, 1); }
function fmt(d) { return d.toISOString().slice(0, 10); }
function toLocalISO(d) {
  // Format Date as "yyyy-MM-ddTHH:mm" suitable for <input type=datetime-local>
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const EMPTY = { id: null, title: "", description: "", start_date: "", end_date: "", color: "#00B8FF", attendees: "" };

export default function CalendarAdmin() {
  const [events, setEvents] = useState([]);
  const [cursor, setCursor] = useState(startOfMonth(new Date()));
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(null); // event in detail drawer

  const load = () => api.get("/admin/calendar/events").then((r) => setEvents(r.data));
  useEffect(() => { load(); }, []);

  const openNew = (defaultDate) => {
    setF({
      ...EMPTY,
      start_date: defaultDate ? toLocalISO(defaultDate) : "",
    });
    setOpen(true);
  };
  const openEdit = (ev) => {
    setF({
      id: ev.id,
      title: ev.title || "",
      description: ev.description || "",
      start_date: ev.start_date ? toLocalISO(new Date(ev.start_date)) : "",
      end_date: ev.end_date ? toLocalISO(new Date(ev.end_date)) : "",
      color: ev.color || "#00B8FF",
      attendees: (ev.attendees || []).map((a) => a.email).join(", "),
    });
    setSelected(null);
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!f.title.trim() || !f.start_date) { toast.error("Title and start date required"); return; }
    setSaving(true);
    try {
      const body = {
        title: f.title,
        description: f.description,
        start_date: new Date(f.start_date).toISOString(),
        end_date: f.end_date ? new Date(f.end_date).toISOString() : null,
        color: f.color,
        attendees: f.attendees.split(/[,\n]/).map((s) => s.trim()).filter(Boolean),
      };
      if (f.id) {
        await api.put(`/admin/calendar/events/${f.id}`, body);
        toast.success("Event updated — new attendees notified by email");
      } else {
        await api.post("/admin/calendar/events", body);
        toast.success("Event created — invites sent");
      }
      setOpen(false); setF(EMPTY); load();
    } catch (err) {
      toast.error("Save failed: " + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this event?")) return;
    try { await api.delete(`/admin/calendar/events/${id}`); toast.success("Event deleted"); setSelected(null); load(); }
    catch { toast.error("Delete failed"); }
  };

  const copyInvite = (eventId, token, email) => {
    const url = `${window.location.origin}/event/${eventId}?t=${token}`;
    navigator.clipboard.writeText(url);
    toast.success(`Invite link for ${email} copied`);
  };

  const days = useMemo(() => {
    const start = startOfMonth(cursor);
    const offset = start.getDay();
    const total = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
    const arr = [];
    for (let i = 0; i < offset; i++) arr.push(null);
    for (let d = 1; d <= total; d++) arr.push(new Date(cursor.getFullYear(), cursor.getMonth(), d));
    return arr;
  }, [cursor]);

  const eventsByDay = useMemo(() => {
    const map = {};
    for (const ev of events) {
      const k = ev.start_date.slice(0, 10);
      (map[k] = map[k] || []).push(ev);
    }
    return map;
  }, [events]);

  const todayKey = fmt(new Date());

  return (
    <div data-testid="admin-calendar">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="label-mono mb-2">/ Calendar</div>
          <h1 className="font-display text-4xl font-bold tracking-tighter">Project Calendar</h1>
          <p className="text-zinc-500 text-sm mt-2">Add attendees and they'll get a magic-link invite by email — no account needed to RSVP.</p>
        </div>
        <button onClick={() => openNew()} className="glass-btn solid" data-testid="event-new"><Plus size={14}/> New Event</button>
      </div>

      <div className="rounded-2xl border border-white/5 bg-[#121215] overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/5">
          <button onClick={() => setCursor(addMonths(cursor, -1))} className="glass-btn ghost p-2" aria-label="Previous month"><ChevronLeft size={14}/></button>
          <div className="font-display text-xl font-bold tracking-tight">
            {cursor.toLocaleDateString("en-US", { month: "long", year: "numeric" })}
          </div>
          <button onClick={() => setCursor(addMonths(cursor, 1))} className="glass-btn ghost p-2" aria-label="Next month"><ChevronRight size={14}/></button>
        </div>
        <div className="grid grid-cols-7 text-center font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-500 border-b border-white/5">
          {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map((d) => <div key={d} className="px-2 py-3">{d}</div>)}
        </div>
        <div className="grid grid-cols-7">
          {days.map((d, i) => (
            <div
              key={i}
              className={`min-h-[120px] p-2 border-b border-r border-white/5 transition-colors ${d ? "hover:bg-white/[0.02] cursor-pointer" : ""}`}
              onClick={d ? () => openNew(d) : undefined}
            >
              {d && (
                <>
                  <div className={`text-xs font-mono mb-1 ${fmt(d) === todayKey ? "text-cyan-400 font-bold" : "text-zinc-500"}`}>
                    {d.getDate()}
                  </div>
                  <div className="space-y-1">
                    {(eventsByDay[fmt(d)] || []).map((ev) => {
                      const yes = (ev.attendees || []).filter((a) => a.rsvp === "yes").length;
                      const total = (ev.attendees || []).length;
                      return (
                        <div
                          key={ev.id}
                          onClick={(e) => { e.stopPropagation(); setSelected(ev); }}
                          className="text-xs p-1.5 rounded cursor-pointer truncate hover:opacity-80 transition-opacity"
                          style={{ background: `${ev.color}15`, color: ev.color, border: `1px solid ${ev.color}40` }}
                          data-testid={`cal-event-${ev.id}`}
                        >
                          <div className="truncate font-medium">{ev.title}</div>
                          {total > 0 && (
                            <div className="text-[9px] opacity-70 mt-0.5">{yes}/{total} confirmed</div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Event detail drawer */}
      {selected && (
        <Modal open={true} onClose={() => setSelected(null)} wide title="" testid="event-detail">
          <div className="space-y-6" data-testid="event-detail-content">
            <div>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div
                    className="inline-block px-3 py-1 rounded-full text-xs font-mono mb-3 border"
                    style={{ background: `${selected.color}20`, color: selected.color, borderColor: `${selected.color}40` }}
                  >
                    {new Date(selected.start_date).toLocaleString("en-US", { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                  </div>
                  <h2 className="font-display text-3xl font-bold tracking-tight">{selected.title}</h2>
                  {selected.description && (
                    <p className="text-zinc-300 mt-3 whitespace-pre-line">{selected.description}</p>
                  )}
                </div>
                <div className="flex gap-2 shrink-0">
                  <button onClick={() => openEdit(selected)} className="glass-btn ghost p-2" aria-label="Edit" data-testid={`event-edit-${selected.id}`}>
                    <Edit2 size={14}/>
                  </button>
                  <button onClick={() => del(selected.id)} className="glass-btn ghost p-2" aria-label="Delete" data-testid={`event-del-${selected.id}`}>
                    <Trash2 size={14}/>
                  </button>
                </div>
              </div>
            </div>

            {/* Attendees */}
            <div>
              <div className="label-mono text-zinc-500 border-b border-white/5 pb-2 mb-3">/ Attendees · {(selected.attendees || []).length}</div>
              {(!selected.attendees || selected.attendees.length === 0) ? (
                <p className="text-zinc-500 text-sm italic">No attendees invited.</p>
              ) : (
                <div className="space-y-2">
                  {selected.attendees.map((a) => (
                    <div key={a.email} className="flex items-center gap-3 px-4 py-3 rounded-xl bg-[#0a0a0c] border border-white/5" data-testid={`att-${a.email}`}>
                      <RsvpBadge rsvp={a.rsvp} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm truncate inline-flex items-center gap-2">
                          <Mail size={11} className="text-zinc-600 shrink-0"/>
                          {a.email}
                        </div>
                        {a.responded_at ? (
                          <div className="text-[10px] text-zinc-600 font-mono">Responded {new Date(a.responded_at).toLocaleString()}</div>
                        ) : (
                          <div className="text-[10px] text-zinc-600 font-mono inline-flex items-center gap-1">
                            <Clock size={9}/> Awaiting response
                          </div>
                        )}
                      </div>
                      <button
                        onClick={() => copyInvite(selected.id, a.token, a.email)}
                        className="glass-btn ghost p-2"
                        aria-label="Copy invite link"
                        title="Copy magic link"
                      >
                        <Copy size={12}/>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </Modal>
      )}

      {/* Create / Edit modal */}
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        wide
        title={f.id ? "Edit Event" : "New Event"}
        testid="event-modal"
      >
        <form onSubmit={save} className="space-y-4" data-testid="event-form">
          <Input label="Title *" required value={f.title} onChange={(v) => setF({ ...f, title: v })} testid="ev-title"/>
          <div className="grid grid-cols-2 gap-3">
            <Input label="Start *" type="datetime-local" required value={f.start_date} onChange={(v) => setF({ ...f, start_date: v })} testid="ev-start"/>
            <Input label="End" type="datetime-local" value={f.end_date} onChange={(v) => setF({ ...f, end_date: v })} testid="ev-end"/>
          </div>
          <div>
            <label className="label-mono block mb-2">Description</label>
            <textarea className="pulse-input pulse-textarea" rows={3} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="ev-desc"/>
          </div>
          <div>
            <label className="label-mono block mb-2">Attendees (emails — comma or newline separated)</label>
            <textarea className="pulse-input pulse-textarea" rows={3} placeholder="alice@example.com, bob@example.com" value={f.attendees} onChange={(e) => setF({ ...f, attendees: e.target.value })} data-testid="ev-attendees"/>
            <p className="text-[11px] text-zinc-600 mt-1.5">Each attendee gets a unique magic-link invite by email. They can RSVP without creating an account.</p>
          </div>
          <div>
            <label className="label-mono block mb-2">Color</label>
            <input type="color" className="pulse-input h-12 w-32" value={f.color} onChange={(e) => setF({ ...f, color: e.target.value })}/>
          </div>
          <button type="submit" disabled={saving} className="glass-btn solid w-full justify-center" data-testid="ev-save">
            {saving ? "Saving..." : (f.id ? "Update Event" : "Create Event & Send Invites")}
          </button>
        </form>
      </Modal>
    </div>
  );
}

function RsvpBadge({ rsvp }) {
  if (rsvp === "yes") return <div className="w-7 h-7 rounded-full bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center text-emerald-400 shrink-0" title="Going"><Check size={14}/></div>;
  if (rsvp === "no") return <div className="w-7 h-7 rounded-full bg-red-500/15 border border-red-500/40 flex items-center justify-center text-red-400 shrink-0" title="Not going"><X size={14}/></div>;
  if (rsvp === "maybe") return <div className="w-7 h-7 rounded-full bg-amber-500/15 border border-amber-500/40 flex items-center justify-center text-amber-400 shrink-0 text-[10px] font-bold" title="Maybe">M</div>;
  return <div className="w-7 h-7 rounded-full bg-zinc-700/30 border border-zinc-700/60 flex items-center justify-center text-zinc-500 shrink-0" title="Pending"><Clock size={12}/></div>;
}

function Input({ label, value, onChange, required = false, type = "text", placeholder = "", testid }) {
  return (
    <div>
      <label className="label-mono block mb-2">{label}</label>
      <input
        className="pulse-input"
        type={type}
        required={required}
        placeholder={placeholder}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
      />
    </div>
  );
}
